import React, { useState, useEffect } from 'react';
import { apiClient } from '../../services/api';
import {
  Plus, Trash2,
  CheckCircle, CheckSquare, BadgeCheck, ShieldCheck,
  Briefcase, Building2, Store, CreditCard, Package, Box, Truck, Wallet,
  User, Users, UserCheck, UserPlus, Heart, ThumbsUp, Smile, MessageCircle, MessageSquare,
  Award, Star, Trophy, Crown, Shield,
  Phone, Mail, Send, Bell, AtSign, Globe, Headphones,
  Clock, Calendar, CalendarCheck, Timer, AlarmClock,
  DollarSign, TrendingUp, TrendingDown, BarChart2, PieChart, Layers,
  Wrench, Settings, Code, Monitor, Smartphone, Laptop, Cpu, Database, Server, Cloud, Wifi, Zap, PenTool, Pencil, Clipboard,
  MapPin, Navigation, Compass, Map, Home, ArrowRight, Link,
  Lock, Key, Eye, EyeOff, Fingerprint,
  FileText, Image, Video, Camera, Mic, Music, BookOpen, GraduationCap,
  Leaf, Sun, Sparkles, Flame, Activity,
  Target, Flag, Download, Upload, RefreshCw, Search, Filter, ChevronRight,
  Scale, Handshake, Sword, Wand, OctagonX,
} from 'lucide-react';
import { LPComponent, LP_COMPONENT_PALETTE } from './lpComponentTypes';
import { BrandTheme } from './LPComponentRenderer';
import { REVFX_PALETTE, REVFX_BY_ID, defaultEffectProps, effectIntegration, isEffectImageProp, isEffectImageArrayProp, isEffectImageItemsProp, type RevfxPropMeta } from './revfxCatalogue';

interface LPPropertiesPanelProps {
  component: LPComponent | null;
  onChange: (id: string, newProps: Record<string, any>) => void;
  projectId?: string | null;
  brandTheme?: BrandTheme;
  allComponents?: LPComponent[];
}

// ── Small reusable form widgets ─────────────────────────────────────────────

const fieldStyle: React.CSSProperties = {
  marginBottom: '16px',
};

const helpTextStyle: React.CSSProperties = {
  margin: '4px 0 0',
  fontSize: '0.72rem',
  color: 'var(--text-secondary)',
  lineHeight: 1.4,
};

const labelStyle: React.CSSProperties = {
  display: 'block',
  fontSize: '0.75rem',
  fontWeight: 600,
  color: 'var(--text-secondary)',
  marginBottom: '4px',
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
};

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '8px 10px',
  border: '1px solid var(--stroke)',
  borderRadius: 'var(--radius-md, 6px)',
  fontSize: '0.875rem',
  background: 'var(--panel-strong)',
  color: 'var(--text)',
  boxSizing: 'border-box',
  outline: 'none',
  fontFamily: 'inherit',
};

const textareaStyle: React.CSSProperties = {
  ...inputStyle,
  resize: 'vertical',
  minHeight: '72px',
};

const subCardStyle: React.CSSProperties = {
  background: 'var(--panel)',
  border: '1px solid var(--stroke)',
  borderRadius: 'var(--radius-md, 6px)',
  padding: '12px',
  marginBottom: '10px',
  position: 'relative',
};

const smallBtnStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: '4px',
  padding: '5px 10px',
  fontSize: '0.75rem',
  fontWeight: 600,
  border: '1px solid var(--stroke)',
  borderRadius: 'var(--radius-md, 6px)',
  background: 'var(--panel-strong)',
  color: 'var(--text)',
  cursor: 'pointer',
};

const dangerBtnStyle: React.CSSProperties = {
  ...smallBtnStyle,
  color: 'var(--danger, #F87171)',
  borderColor: 'var(--danger, #F87171)',
  background: 'transparent',
  padding: '4px 7px',
};

const sectionTitleStyle: React.CSSProperties = {
  fontSize: '1rem',
  fontWeight: 700,
  color: 'var(--text)',
  margin: '0 0 16px',
  paddingBottom: '10px',
  borderBottom: '1px solid var(--stroke)',
};

// ── Icon picker ──────────────────────────────────────────────────────────────

const ICON_OPTIONS = [
  // Check / validation
  'check-circle', 'check-square', 'badge-check', 'shield-check',
  // Business
  'briefcase', 'building-2', 'store', 'credit-card', 'package', 'truck', 'wallet',
  // People
  'user', 'users', 'user-check', 'user-plus', 'heart', 'thumbs-up', 'smile',
  'message-circle', 'message-square',
  // Achievement
  'award', 'star', 'trophy', 'crown', 'shield',
  // Communication
  'phone', 'mail', 'send', 'bell', 'globe', 'headphones',
  // Time
  'clock', 'timer', 'calendar', 'calendar-check', 'alarm-clock',
  // Finance / analytics
  'dollar-sign', 'trending-up', 'trending-down', 'bar-chart-2', 'pie-chart', 'layers',
  // Tools / tech
  'wrench', 'settings', 'code', 'monitor', 'smartphone', 'laptop', 'cpu',
  'database', 'server', 'cloud', 'wifi', 'zap', 'pen-tool', 'pencil', 'clipboard',
  // Navigation / location
  'map-pin', 'navigation', 'compass', 'home', 'arrow-right', 'link',
  // Security
  'lock', 'key', 'eye', 'fingerprint',
  // Content / media
  'file-text', 'image', 'video', 'camera', 'mic', 'book-open', 'graduation-cap',
  // Growth / misc
  'leaf', 'sun', 'sparkles', 'flame', 'activity', 'target', 'flag',
  'download', 'upload', 'search', 'filter',
  // New
  'scale', 'handshake', 'sword', 'wand', 'octagon-x',
];

const PANEL_ICON_MAP: Record<string, React.ComponentType<{ size?: number }>> = {
  'check-circle': CheckCircle, 'check': CheckSquare, 'check-square': CheckSquare,
  'badge-check': BadgeCheck, 'shield-check': ShieldCheck,
  'briefcase': Briefcase, 'building-2': Building2, 'store': Store,
  'credit-card': CreditCard, 'package': Package, 'box': Box, 'truck': Truck, 'wallet': Wallet,
  'user': User, 'users': Users, 'user-check': UserCheck, 'user-plus': UserPlus,
  'heart': Heart, 'thumbs-up': ThumbsUp, 'smile': Smile,
  'message-circle': MessageCircle, 'message': MessageCircle, 'message-square': MessageSquare,
  'award': Award, 'star': Star, 'trophy': Trophy, 'crown': Crown, 'shield': Shield,
  'phone': Phone, 'mail': Mail, 'send': Send, 'bell': Bell, 'at-sign': AtSign,
  'globe': Globe, 'headphones': Headphones,
  'clock': Clock, 'timer': Timer, 'calendar': Calendar, 'calendar-check': CalendarCheck,
  'alarm-clock': AlarmClock,
  'dollar': DollarSign, 'dollar-sign': DollarSign, 'trending-up': TrendingUp,
  'trending-down': TrendingDown, 'chart': BarChart2, 'bar-chart': BarChart2,
  'bar-chart-2': BarChart2, 'pie-chart': PieChart, 'layers': Layers,
  'wrench': Wrench, 'tool': Wrench, 'settings': Settings, 'code': Code,
  'monitor': Monitor, 'smartphone': Smartphone, 'laptop': Laptop, 'cpu': Cpu,
  'database': Database, 'server': Server, 'cloud': Cloud, 'wifi': Wifi,
  'zap': Zap, 'bolt': Zap, 'pen-tool': PenTool, 'pencil': Pencil, 'clipboard': Clipboard,
  'map-pin': MapPin, 'location': MapPin, 'navigation': Navigation, 'compass': Compass,
  'map': Map, 'home': Home, 'arrow-right': ArrowRight, 'link': Link,
  'lock': Lock, 'key': Key, 'eye': Eye, 'eye-off': EyeOff, 'fingerprint': Fingerprint,
  'file': FileText, 'file-text': FileText, 'image': Image, 'video': Video,
  'camera': Camera, 'mic': Mic, 'music': Music, 'book-open': BookOpen,
  'graduation-cap': GraduationCap,
  'leaf': Leaf, 'sun': Sun, 'sparkles': Sparkles, 'flame': Flame, 'activity': Activity,
  'target': Target, 'flag': Flag, 'download': Download, 'upload': Upload,
  'refresh': RefreshCw, 'search': Search, 'filter': Filter, 'chevron-right': ChevronRight,
  'scale': Scale, 'handshake': Handshake, 'sword': Sword, 'wand': Wand, 'octagon-x': OctagonX,
};

function IconPickerField({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
}) {
  const [open, setOpen] = React.useState(false);
  const IconPreview = value ? PANEL_ICON_MAP[value.toLowerCase()] : null;

  return (
    <div style={fieldStyle}>
      <label style={labelStyle}>{label}</label>
      <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
        <div style={{ width: '32px', height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--panel-strong)', border: '1px solid var(--stroke)', borderRadius: 'var(--radius-md)' }}>
          {IconPreview ? <IconPreview size={16} /> : <span style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>?</span>}
        </div>
        <input
          type="text"
          style={{ ...inputStyle, flex: 1 }}
          value={value ?? ''}
          onChange={(e) => onChange(e.target.value)}
          placeholder="icon name"
        />
        <button style={{ ...smallBtnStyle, padding: '7px 10px' }} onClick={() => setOpen(o => !o)} type="button">
          {open ? '▲' : '▼'}
        </button>
      </div>
      {open && (
        <div style={{
          marginTop: '6px', display: 'flex', flexWrap: 'wrap', gap: '4px',
          background: 'var(--panel)', border: '1px solid var(--stroke)',
          borderRadius: 'var(--radius-md)', padding: '8px',
        }}>
          {ICON_OPTIONS.map((name) => {
            const Ic = PANEL_ICON_MAP[name];
            return (
              <button
                key={name}
                title={name}
                type="button"
                onClick={() => { onChange(name); setOpen(false); }}
                style={{
                  width: '32px', height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  border: value === name ? '2px solid var(--primary-500, #2177FF)' : '1px solid var(--stroke)',
                  borderRadius: 'var(--radius-md)', background: value === name ? 'var(--primary-500,#2177FF)22' : 'var(--panel-strong)',
                  cursor: 'pointer', padding: 0,
                  color: 'var(--text)',
                }}
              >
                {Ic ? <Ic size={14} /> : <span style={{ fontSize: '10px' }}>{name.charAt(0).toUpperCase()}</span>}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── Colour picker ────────────────────────────────────────────────────────────

function ColorPickerField({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
}) {
  const colorInputRef = React.useRef<HTMLInputElement>(null);
  const hex = value && value.startsWith('#') ? value : '#ffffff';

  return (
    <div style={fieldStyle}>
      <label style={labelStyle}>{label}</label>
      <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
        {/* Colour swatch — clicking it opens the native colour picker */}
        <div
          onClick={() => colorInputRef.current?.click()}
          style={{
            width: '36px', height: '36px', borderRadius: '6px', flexShrink: 0,
            background: hex, border: '1px solid var(--stroke)', cursor: 'pointer',
            boxShadow: 'inset 0 0 0 1px rgba(0,0,0,0.1)',
          }}
        />
        <input
          ref={colorInputRef}
          type="color"
          value={hex}
          onChange={(e) => onChange(e.target.value)}
          style={{ position: 'absolute', opacity: 0, width: 0, height: 0, pointerEvents: 'none' }}
        />
        {/* Hex text input */}
        <input
          type="text"
          style={{ ...inputStyle, flex: 1, fontFamily: 'monospace' }}
          value={value ?? ''}
          maxLength={7}
          onChange={(e) => {
            const v = e.target.value;
            if (/^#[0-9A-Fa-f]{0,6}$/.test(v) || v === '') onChange(v);
          }}
          placeholder="#000000"
        />
      </div>
    </div>
  );
}

// ── Section background editor (universal) ────────────────────────────────────

const GRADIENT_DIRECTIONS = [
  { value: '135deg', label: '↘ Diagonal (default)' },
  { value: '180deg', label: '↓ Top to Bottom' },
  { value: '90deg',  label: '→ Left to Right' },
  { value: '45deg',  label: '↗ Diagonal (alt)' },
  { value: '0deg',   label: '↑ Bottom to Top' },
  { value: '270deg', label: '← Right to Left' },
];

function BackgroundEditor({
  props,
  update,
}: {
  props: Record<string, any>;
  update: (p: Record<string, any>) => void;
}) {
  const bgType = props._bg_type || 'default';

  return (
    <div style={{ marginBottom: '20px', paddingBottom: '16px', borderBottom: '1px solid var(--stroke)' }}>
      <label style={{ ...labelStyle, marginBottom: '8px' }}>Section Background</label>
      <div style={{ display: 'flex', gap: '6px', marginBottom: '12px', flexWrap: 'wrap' }}>
        {(['default', 'solid', 'gradient'] as const).map((t) => (
          <button
            key={t}
            type="button"
            onClick={() => update({ ...props, _bg_type: t })}
            style={{
              ...smallBtnStyle,
              background: bgType === t ? 'var(--primary-500,#2177FF)' : 'var(--panel-strong)',
              color: bgType === t ? '#fff' : 'var(--text)',
              border: bgType === t ? 'none' : '1px solid var(--stroke)',
              textTransform: 'capitalize',
            }}
          >
            {t}
          </button>
        ))}
      </div>
      {bgType === 'solid' && (
        <ColorPickerField
          label="Background colour"
          value={props._bg_color || '#ffffff'}
          onChange={(v) => update({ ...props, _bg_color: v })}
        />
      )}
      {bgType === 'gradient' && (
        <>
          <ColorPickerField
            label="Gradient — colour 1 (from)"
            value={props._bg_gradient_from || '#2177FF'}
            onChange={(v) => update({ ...props, _bg_gradient_from: v })}
          />
          <ColorPickerField
            label="Gradient — colour 2 (to)"
            value={props._bg_gradient_to || '#0f172a'}
            onChange={(v) => update({ ...props, _bg_gradient_to: v })}
          />
          <div style={fieldStyle}>
            <label style={labelStyle}>Direction</label>
            <select
              style={inputStyle}
              value={props._bg_gradient_angle || '135deg'}
              onChange={(e) => update({ ...props, _bg_gradient_angle: e.target.value })}
            >
              {GRADIENT_DIRECTIONS.map((d) => (
                <option key={d.value} value={d.value}>{d.label}</option>
              ))}
            </select>
          </div>
        </>
      )}
      {bgType !== 'default' && (
        <div style={{ marginTop: '4px' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '6px' }}>
            <ColorPickerField
              label="Text colour"
              value={props._text_color || '#0f172a'}
              onChange={(v) => update({ ...props, _text_color: v })}
            />
          </div>
          <div style={{ display: 'flex', gap: '6px' }}>
            <button
              type="button"
              style={{ ...smallBtnStyle, flex: 1, fontSize: '0.7rem' }}
              onClick={() => update({ ...props, _text_color: '#0f172a' })}
            >Dark text</button>
            <button
              type="button"
              style={{ ...smallBtnStyle, flex: 1, fontSize: '0.7rem', background: '#0f172a', color: '#fff', border: 'none' }}
              onClick={() => update({ ...props, _text_color: '#ffffff' })}
            >Light text</button>
          </div>
        </div>
      )}
    </div>
  );
}

function ImageUploadField({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (url: string) => void;
}) {
  const [uploading, setUploading] = React.useState(false);
  const fileRef = React.useRef<HTMLInputElement>(null);

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      // Use the shared apiClient (not raw fetch) so the auth header + 401 token
      // refresh are handled — raw fetch with a manually-read token fails once the
      // access token expires even though the session is still valid.
      // Override the apiClient's default application/json so axios sets the
      // multipart/form-data boundary FastAPI needs to parse the file (else 422).
      const res = await apiClient.post('/brand-guides/upload-asset', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      onChange(res.data.url);
    } catch (err: any) {
      const detail = err?.response?.data?.detail;
      alert(typeof detail === 'string' ? detail : (err?.message || 'Upload failed. Please try again.'));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  return (
    <div style={fieldStyle}>
      <label style={labelStyle}>{label}</label>
      {value && (
        <img
          src={value}
          alt="preview"
          style={{ maxWidth: '100%', maxHeight: '80px', objectFit: 'contain', marginBottom: '6px', borderRadius: '4px', border: '1px solid var(--stroke)', background: '#f8f9fa' }}
        />
      )}
      <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
        <input
          type="text"
          style={{ ...inputStyle, flex: 1 }}
          value={value ?? ''}
          onChange={(e) => onChange(e.target.value)}
          placeholder="URL or upload below"
        />
        <button
          type="button"
          style={{ ...smallBtnStyle, padding: '7px 10px', whiteSpace: 'nowrap' }}
          onClick={() => fileRef.current?.click()}
          disabled={uploading}
        >
          {uploading ? '…' : '↑ Upload'}
        </button>
      </div>
      <input ref={fileRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleUpload} />
    </div>
  );
}

const readonlyStyle: React.CSSProperties = {
  ...inputStyle,
  opacity: 0.5,
  cursor: 'not-allowed',
  background: 'var(--panel)',
};

// ── Field builders ──────────────────────────────────────────────────────────

function ImageArrayField({
  label,
  values,
  onChange,
  helpText,
}: {
  label: string;
  values: string[];
  onChange: (urls: string[]) => void;
  helpText?: string;
}) {
  const list = Array.isArray(values) ? values : [];
  const setAt = (i: number, url: string) => onChange(list.map((v, j) => (j === i ? url : v)));
  const removeAt = (i: number) => onChange(list.filter((_, j) => j !== i));
  const add = () => onChange([...list, '']);
  return (
    <div style={fieldStyle}>
      <label style={labelStyle}>{label}</label>
      {helpText && <p style={{ ...helpTextStyle, marginTop: 0, marginBottom: '8px' }}>{helpText}</p>}
      {list.map((url, i) => (
        <div key={i} style={{ display: 'flex', gap: '6px', alignItems: 'flex-start', marginBottom: '8px' }}>
          <div style={{ flex: 1 }}>
            <ImageUploadField label={`Image ${i + 1}`} value={url} onChange={(u) => setAt(i, u)} />
          </div>
          <button type="button" onClick={() => removeAt(i)} title="Remove image"
            style={{ ...smallBtnStyle, padding: '6px', marginTop: '22px', color: 'var(--danger-600, #dc2626)' }}>
            <Trash2 size={14} />
          </button>
        </div>
      ))}
      <button type="button" onClick={add} style={{ ...smallBtnStyle, display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
        <Plus size={14} /> Add image
      </button>
    </div>
  );
}

function TextField({
  label,
  value,
  onChange,
  multiline = false,
  type = 'text',
  readonly = false,
  placeholder,
  helpText,
}: {
  label: string;
  value: string;
  onChange?: (v: string) => void;
  multiline?: boolean;
  type?: string;
  readonly?: boolean;
  placeholder?: string;
  helpText?: string;
}) {
  if (multiline) {
    return (
      <div style={fieldStyle}>
        <label style={labelStyle}>{label}</label>
        <textarea
          style={readonly ? { ...textareaStyle, ...readonlyStyle } : textareaStyle}
          value={value ?? ''}
          readOnly={readonly}
          onChange={(e) => onChange?.(e.target.value)}
        />
      </div>
    );
  }
  return (
    <div style={fieldStyle}>
      <label style={labelStyle}>{label}</label>
      <input
        type={type}
        style={readonly ? readonlyStyle : inputStyle}
        value={value ?? ''}
        readOnly={readonly}
        placeholder={placeholder}
        onChange={(e) => onChange?.(e.target.value)}
      />
      {helpText && <p style={{ margin: '4px 0 0', fontSize: '0.72rem', color: 'var(--text-secondary)', lineHeight: 1.4 }}>{helpText}</p>}
    </div>
  );
}

function NumberField({
  label,
  value,
  onChange,
  min,
  max,
}: {
  label: string;
  value: number;
  onChange: (v: number) => void;
  min?: number;
  max?: number;
}) {
  return (
    <div style={fieldStyle}>
      <label style={labelStyle}>{label}</label>
      <input
        type="number"
        style={inputStyle}
        value={value ?? 0}
        min={min}
        max={max}
        onChange={(e) => onChange(Number(e.target.value))}
      />
    </div>
  );
}

function BooleanField({
  label,
  value,
  onChange,
}: {
  label: string;
  value: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <div style={{ ...fieldStyle, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
      <label style={{ ...labelStyle, marginBottom: 0 }}>{label}</label>
      <input
        type="checkbox"
        checked={!!value}
        onChange={(e) => onChange(e.target.checked)}
        style={{ width: '16px', height: '16px', cursor: 'pointer', accentColor: 'var(--primary-500, #2177FF)' }}
      />
    </div>
  );
}

// ── Per-type editors ────────────────────────────────────────────────────────

function HeroEditor({
  props,
  update,
  secondaryColor,
  allComponents,
  projectId,
}: {
  props: Record<string, any>;
  update: (p: Record<string, any>) => void;
  secondaryColor: string;
  allComponents?: LPComponent[];
  projectId?: string | null;
}) {
  return (
    <>
      <div style={fieldStyle}>
        <label style={labelStyle}>Layout</label>
        <select
          style={inputStyle}
          value={props.layout ?? 'centered'}
          onChange={(e) => update({ ...props, layout: e.target.value })}
        >
          <option value="centered">Centred (full-width text + CTA)</option>
          <option value="split_form">Split — text left, lead form right</option>
          <option value="split_image">Split — text left, image right</option>
        </select>
      </div>
      {props.layout === 'split_form' && (
        <TextField
          label="Form title"
          value={props.form_title ?? ''}
          onChange={(v) => update({ ...props, form_title: v })}
        />
      )}
      {props.layout === 'split_form' && (
        <TextField
          label="Form submit button text"
          value={props.form_submit_text ?? ''}
          onChange={(v) => update({ ...props, form_submit_text: v })}
        />
      )}
      {props.layout === 'split_form' && (
        <LeadFormPicker
          label="Lead capture form (optional)"
          propKey="form_id"
          props={props}
          update={update}
          projectId={projectId}
          mode="id"
        />
      )}
      <TextField
        label="Badge text"
        value={props.badge ?? ''}
        onChange={(v) => update({ ...props, badge: v })}
      />
      <TextField
        label="Headline"
        value={props.headline ?? ''}
        multiline
        onChange={(v) => update({ ...props, headline: v })}
      />
      <TextField
        label="Sub-headline"
        value={props.subheadline ?? ''}
        multiline
        onChange={(v) => update({ ...props, subheadline: v })}
      />
      <TextField
        label="Primary CTA button text"
        value={props.cta_text ?? ''}
        onChange={(v) => update({ ...props, cta_text: v })}
      />
      <LinkPicker label="Primary CTA link" value={props.cta_url ?? ''} onChange={(v) => update({ ...props, cta_url: v })} allComponents={allComponents} />
      <TextField
        label="Secondary CTA text (optional)"
        value={props.secondary_cta_text ?? ''}
        onChange={(v) => update({ ...props, secondary_cta_text: v })}
      />
      <LinkPicker label="Secondary CTA link" value={props.secondary_cta_url ?? ''} onChange={(v) => update({ ...props, secondary_cta_url: v })} allComponents={allComponents} />
      <ColorPickerField
        label="Button colour"
        value={props.button_color || secondaryColor}
        onChange={(v) => update({ ...props, button_color: v })}
      />
      <BooleanField
        label="Pill-shaped buttons"
        value={props.pill_buttons !== false}
        onChange={(v) => update({ ...props, pill_buttons: v })}
      />
      <div style={fieldStyle}>
        <label style={labelStyle}>Background style</label>
        <select
          style={inputStyle}
          value={props.background_style ?? 'gradient'}
          onChange={(e) =>
            update({ ...props, background_style: e.target.value })
          }
        >
          <option value="gradient">Gradient</option>
          <option value="solid">Solid colour</option>
          <option value="image">Image</option>
        </select>
      </div>
      {props.background_style === 'image' && (
        <ImageUploadField
          label="Background image"
          value={props.background_image ?? ''}
          onChange={(v) => update({ ...props, background_image: v })}
        />
      )}
      {props.layout === 'split_image' && (
        <ImageUploadField
          label="Right-side image"
          value={props.split_image ?? ''}
          onChange={(v) => update({ ...props, split_image: v })}
        />
      )}
    </>
  );
}

function ItemsEditor({
  label,
  items,
  fields,
  onUpdate,
}: {
  label: string;
  items: any[];
  fields: Array<{ key: string; label: string; multiline?: boolean }>;
  onUpdate: (items: any[]) => void;
}) {
  const addItem = () => {
    const empty: Record<string, string> = {};
    fields.forEach((f) => (empty[f.key] = ''));
    onUpdate([...items, empty]);
  };

  const removeItem = (i: number) => {
    onUpdate(items.filter((_, idx) => idx !== i));
  };

  const updateItem = (i: number, key: string, value: string) => {
    const updated = items.map((item, idx) =>
      idx === i ? { ...item, [key]: value } : item
    );
    onUpdate(updated);
  };

  return (
    <div style={fieldStyle}>
      <label style={labelStyle}>{label}</label>
      {items.map((item, i) => (
        <div key={i} style={subCardStyle}>
          <button
            style={{ ...dangerBtnStyle, position: 'absolute', top: '8px', right: '8px' }}
            onClick={() => removeItem(i)}
            title="Remove"
          >
            <Trash2 size={12} />
          </button>
          {fields.map((f) => (
            <div key={f.key} style={{ marginBottom: '8px' }}>
              {f.key === 'icon' ? (
                <IconPickerField
                  label={f.label}
                  value={item[f.key] ?? ''}
                  onChange={(v) => updateItem(i, f.key, v)}
                />
              ) : f.multiline ? (
                <>
                  <label style={{ ...labelStyle, marginBottom: '2px' }}>{f.label}</label>
                  <textarea
                    style={textareaStyle}
                    value={item[f.key] ?? ''}
                    onChange={(e) => updateItem(i, f.key, e.target.value)}
                  />
                </>
              ) : (
                <>
                  <label style={{ ...labelStyle, marginBottom: '2px' }}>{f.label}</label>
                  <input
                    type="text"
                    style={inputStyle}
                    value={item[f.key] ?? ''}
                    onChange={(e) => updateItem(i, f.key, e.target.value)}
                  />
                </>
              )}
            </div>
          ))}
        </div>
      ))}
      <button style={smallBtnStyle} onClick={addItem}>
        <Plus size={12} />
        Add {label.replace(/s$/, '')}
      </button>
    </div>
  );
}

function StringListEditor({
  label,
  items,
  onUpdate,
}: {
  label: string;
  items: string[];
  onUpdate: (items: string[]) => void;
}) {
  const addItem = () => onUpdate([...items, '']);
  const removeItem = (i: number) => onUpdate(items.filter((_, idx) => idx !== i));
  const updateItem = (i: number, value: string) =>
    onUpdate(items.map((item, idx) => (idx === i ? value : item)));

  return (
    <div style={fieldStyle}>
      <label style={labelStyle}>{label}</label>
      {items.map((item, i) => (
        <div
          key={i}
          style={{ display: 'flex', gap: '6px', marginBottom: '6px' }}
        >
          <input
            type="text"
            style={{ ...inputStyle, flex: 1 }}
            value={item}
            onChange={(e) => updateItem(i, e.target.value)}
          />
          <button style={dangerBtnStyle} onClick={() => removeItem(i)}>
            <Trash2 size={12} />
          </button>
        </div>
      ))}
      <button style={smallBtnStyle} onClick={addItem}>
        <Plus size={12} />
        Add step
      </button>
    </div>
  );
}

// ── Logo cloud editor ──────────────────────────────────────────────────────

function LogoCloudEditor({
  logos,
  onUpdate,
}: {
  logos: any[];
  onUpdate: (logos: any[]) => void;
}) {
  const addLogo = () => onUpdate([...logos, { name: '', logo_url: '', width: 120 }]);
  const removeLogo = (i: number) => onUpdate(logos.filter((_, idx) => idx !== i));
  const updateLogo = (i: number, key: string, value: any) =>
    onUpdate(logos.map((l, idx) => (idx === i ? { ...l, [key]: value } : l)));

  return (
    <div style={fieldStyle}>
      <label style={labelStyle}>Logos / Badges</label>
      {logos.map((logo, i) => (
        <div key={i} style={subCardStyle}>
          <button
            style={{ ...dangerBtnStyle, position: 'absolute', top: '8px', right: '8px' }}
            onClick={() => removeLogo(i)}
            title="Remove"
          >
            <Trash2 size={12} />
          </button>
          <ImageUploadField
            label="Logo image"
            value={logo.logo_url ?? ''}
            onChange={(v) => updateLogo(i, 'logo_url', v)}
          />
          <div style={{ marginBottom: '8px' }}>
            <label style={{ ...labelStyle, marginBottom: '2px' }}>Name / Alt text</label>
            <input type="text" style={inputStyle} value={logo.name ?? ''} onChange={(e) => updateLogo(i, 'name', e.target.value)} />
          </div>
        </div>
      ))}
      <button style={smallBtnStyle} onClick={addLogo}>
        <Plus size={12} />
        Add logo
      </button>
    </div>
  );
}

// ── Testimonials editor with avatar upload ───────────────────────────────────

function TestimonialsItemsEditor({
  items,
  onUpdate,
}: {
  items: any[];
  onUpdate: (items: any[]) => void;
}) {
  const addItem = () =>
    onUpdate([...items, { quote: '', author: '', role: '', company: '', avatar_url: '' }]);
  const removeItem = (i: number) => onUpdate(items.filter((_, idx) => idx !== i));
  const updateItem = (i: number, key: string, value: string) =>
    onUpdate(items.map((item, idx) => (idx === i ? { ...item, [key]: value } : item)));

  return (
    <div style={fieldStyle}>
      <label style={labelStyle}>Testimonials</label>
      {items.map((item, i) => (
        <div key={i} style={subCardStyle}>
          <button
            style={{ ...dangerBtnStyle, position: 'absolute', top: '8px', right: '8px' }}
            onClick={() => removeItem(i)}
            title="Remove"
          >
            <Trash2 size={12} />
          </button>
          <ImageUploadField
            label="Avatar photo (optional)"
            value={item.avatar_url ?? ''}
            onChange={(v) => updateItem(i, 'avatar_url', v)}
          />
          <div style={{ marginBottom: '8px' }}>
            <label style={{ ...labelStyle, marginBottom: '2px' }}>Quote</label>
            <textarea
              style={textareaStyle}
              value={item.quote ?? ''}
              onChange={(e) => updateItem(i, 'quote', e.target.value)}
            />
          </div>
          <div style={{ marginBottom: '8px' }}>
            <label style={{ ...labelStyle, marginBottom: '2px' }}>Author name</label>
            <input type="text" style={inputStyle} value={item.author ?? ''} onChange={(e) => updateItem(i, 'author', e.target.value)} />
          </div>
          <div style={{ marginBottom: '8px' }}>
            <label style={{ ...labelStyle, marginBottom: '2px' }}>Role / Title</label>
            <input type="text" style={inputStyle} value={item.role ?? ''} onChange={(e) => updateItem(i, 'role', e.target.value)} />
          </div>
          <div style={{ marginBottom: '8px' }}>
            <label style={{ ...labelStyle, marginBottom: '2px' }}>Company (optional)</label>
            <input type="text" style={inputStyle} value={item.company ?? ''} onChange={(e) => updateItem(i, 'company', e.target.value)} />
          </div>
        </div>
      ))}
      <button style={smallBtnStyle} onClick={addItem}>
        <Plus size={12} />
        Add testimonial
      </button>
    </div>
  );
}

// ── Quiz picker ──────────────────────────────────────────────────────────────

function QuizPicker({ props, update, projectId }: {
  props: Record<string, any>;
  update: (p: Record<string, any>) => void;
  projectId?: string | null;
}) {
  const [quizzes, setQuizzes] = useState<Array<{ quiz_id: string; name: string }>>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!projectId) return;
    setLoading(true);
    apiClient.get(`/quizzes/?project_id=${projectId}`)
      .then((res) => setQuizzes(res.data || []))
      .catch(() => setQuizzes([]))
      .finally(() => setLoading(false));
  }, [projectId]);

  return (
    <>
      <TextField
        label="Section title"
        value={props.title ?? ''}
        onChange={(v) => update({ ...props, title: v })}
      />
      <div style={fieldStyle}>
        <label style={labelStyle}>Quiz</label>
        {loading ? (
          <p style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>Loading quizzes…</p>
        ) : quizzes.length === 0 ? (
          <p style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', fontStyle: 'italic' }}>
            {projectId ? 'No quizzes in this project — create one first.' : 'No project associated.'}
          </p>
        ) : (
          <select
            value={props.quiz_id ?? ''}
            onChange={(e) => {
              const selected = quizzes.find(q => q.quiz_id === e.target.value);
              update({ ...props, quiz_id: e.target.value, quiz_name: selected?.name || '' });
            }}
            style={{
              width: '100%', padding: '8px 10px',
              border: '1px solid var(--stroke)', borderRadius: '6px',
              fontSize: '0.875rem', background: 'var(--panel)',
              color: 'var(--text)', outline: 'none',
            }}
          >
            <option value="">— Select a quiz —</option>
            {quizzes.map((q) => (
              <option key={q.quiz_id} value={q.quiz_id}>{q.name || q.quiz_id}</option>
            ))}
          </select>
        )}
        {props.quiz_id && quizzes.length > 0 && (
          <p style={{ fontSize: '0.72rem', color: 'var(--text-secondary)', marginTop: '4px' }}>
            Selected: {quizzes.find(q => q.quiz_id === props.quiz_id)?.name || props.quiz_id}
          </p>
        )}
      </div>
    </>
  );
}

// ── Link picker — dropdown of page anchors + custom URL ─────────────────────

const SECTION_TYPE_LABELS: Record<string, string> = {
  lp_hero: 'Hero', lp_features: 'Features', lp_services: 'Services',
  lp_stats: 'Stats', lp_process: 'Process', lp_testimonials: 'Testimonials',
  lp_faq: 'FAQ', lp_quiz: 'Quiz', lp_lead_form: 'Lead Form',
  lp_cta: 'CTA', lp_pricing: 'Pricing', lp_logo_cloud: 'Logo Cloud',
  lp_video: 'Video', lp_scarcity_timer: 'Timer', lp_vsl: 'VSL',
  lp_stock: 'Stock', lp_authority: 'Authority', lp_roi_calculator: 'ROI Calculator',
  lp_header: 'Header', lp_footer: 'Footer', lp_product: 'Product',
  lp_share: 'Share', lp_exit_popup: 'Exit Popup', lp_social_proof_popup: 'Social Proof',
};

function LinkPicker({ label, value, onChange, allComponents }: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  allComponents?: LPComponent[];
}) {
  const [isCustom, setIsCustom] = React.useState(false);

  // Build options: #hero-form, #lead-form first, then page sections
  const options: Array<{ value: string; label: string }> = [
    { value: '#hero-form', label: 'Hero Form (Full Name focus)' },
    { value: '#lead-form', label: 'Lead Capture Form' },
  ];

  // Add anchors for each section on the page
  if (allComponents) {
    for (const comp of allComponents) {
      if (comp.type === 'lp_header' || comp.type === 'lp_footer' || comp.type === 'lp_exit_popup' || comp.type === 'lp_social_proof_popup') continue;
      const sectionLabel = SECTION_TYPE_LABELS[comp.type] || comp.type.replace('lp_', '');
      // Use the component's scroll-margin anchor ID pattern
      const title = comp.props?.title || comp.props?.headline || sectionLabel;
      const anchorId = comp.type.replace('lp_', '');
      const anchor = `#${anchorId}`;
      // Avoid duplicates with hero-form / lead-form
      if (anchor === '#lead_form' || anchor === '#hero') continue;
      if (!options.some(o => o.value === anchor)) {
        options.push({ value: anchor, label: `${sectionLabel}: ${title.slice(0, 40)}` });
      }
    }
  }

  options.push({ value: '__custom__', label: '— Custom URL —' });

  // Check if current value matches any option
  const isKnown = options.some(o => o.value === value);
  const showCustom = isCustom || (!isKnown && value && value !== '');

  return (
    <div style={fieldStyle}>
      <label style={labelStyle}>{label}</label>
      {!showCustom ? (
        <select
          style={{ width: '100%', padding: '8px 10px', border: '1px solid var(--stroke)', borderRadius: '6px', fontSize: '0.85rem', background: 'var(--panel)', color: 'var(--text)', outline: 'none' }}
          value={value || '#lead-form'}
          onChange={e => {
            if (e.target.value === '__custom__') {
              setIsCustom(true);
              onChange('');
            } else {
              onChange(e.target.value);
            }
          }}
        >
          {options.map(o => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
      ) : (
        <div style={{ display: 'flex', gap: '6px' }}>
          <input
            style={{ flex: 1, padding: '8px 10px', border: '1px solid var(--stroke)', borderRadius: '6px', fontSize: '0.85rem', background: 'var(--panel)', color: 'var(--text)', outline: 'none' }}
            value={value}
            onChange={e => onChange(e.target.value)}
            placeholder="https://example.com or #section"
          />
          <button
            onClick={() => { setIsCustom(false); onChange('#lead-form'); }}
            style={{ background: 'none', border: '1px solid var(--stroke)', borderRadius: '6px', padding: '4px 8px', fontSize: '0.75rem', cursor: 'pointer', color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}
          >
            Presets
          </button>
        </div>
      )}
    </div>
  );
}


function LeadFormPicker({ label = 'Link to lead form', propKey = 'button_url', props, update, projectId, mode = 'anchor' }: {
  label?: string;
  propKey?: string;
  props: Record<string, any>;
  update: (p: Record<string, any>) => void;
  projectId?: string | null;
  /** 'anchor' stores #lf-{id} (for link targets), 'id' stores raw form_id (for form binding) */
  mode?: 'anchor' | 'id';
}) {
  const [forms, setForms] = useState<Array<{ form_id: string; name: string }>>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!projectId) return;
    setLoading(true);
    apiClient.get(`/lead-capture/forms?project_id=${projectId}`)
      .then((res) => setForms(res.data || []))
      .catch(() => setForms([]))
      .finally(() => setLoading(false));
  }, [projectId]);

  const currentValue = props[propKey] ?? '';
  const isIdMode = mode === 'id';

  return (
    <div style={fieldStyle}>
      <label style={labelStyle}>{label}</label>
      {loading ? (
        <p style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>Loading forms…</p>
      ) : forms.length === 0 ? (
        isIdMode ? (
          <p style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>No lead forms found for this project. Create one in the Lead Capture module.</p>
        ) : (
          <input
            type="text"
            value={currentValue}
            onChange={(e) => update({ ...props, [propKey]: e.target.value })}
            placeholder="#lead-form or URL"
            style={{ width: '100%', padding: '8px 10px', border: '1px solid var(--stroke)', borderRadius: '6px', fontSize: '0.875rem', background: 'var(--panel)', color: 'var(--text)', boxSizing: 'border-box' }}
          />
        )
      ) : (
        <select
          value={currentValue}
          onChange={(e) => update({ ...props, [propKey]: e.target.value || null })}
          style={{ width: '100%', padding: '8px 10px', border: '1px solid var(--stroke)', borderRadius: '6px', fontSize: '0.875rem', background: 'var(--panel)', color: 'var(--text)', outline: 'none' }}
        >
          {isIdMode ? (
            <option value="">None (use default fields)</option>
          ) : (
            <option value="#lead-form">— Page lead form (default) —</option>
          )}
          {forms.map((f) => (
            <option key={f.form_id} value={isIdMode ? f.form_id : `#lf-${f.form_id}`}>{f.name || f.form_id}</option>
          ))}
        </select>
      )}
    </div>
  );
}

// ── Product picker ───────────────────────────────────────────────────────────

const PRODUCT_COLOR_KEYS = [
  'title_color', 'subtitle_color', 'name_color', 'description_color',
  'price_color', 'button_color', 'button_text_color', 'guarantee_color',
];

function ProductPicker({ props, update, primaryColor = '#2177FF' }: {
  props: Record<string, any>;
  update: (p: Record<string, any>) => void;
  primaryColor?: string;
}) {
  const [products, setProducts] = useState<Array<{
    product_id: string; name: string; cover_image_url?: string;
    short_description?: string; description?: string;
    variants: Array<{ variant_id: string; label: string; price: number; currency: string; billing_type: string; active: boolean; gateway_price_id?: string }>;
  }>>([]);
  const [loading, setLoading] = useState(false);
  const selectStyle: React.CSSProperties = {
    width: '100%', padding: '8px 10px',
    border: '1px solid var(--stroke)', borderRadius: '6px',
    fontSize: '0.875rem', background: 'var(--panel)',
    color: 'var(--text)', outline: 'none',
  };

  useEffect(() => {
    setLoading(true);
    apiClient.get('/products/')
      .then((res) => setProducts(res.data || []))
      .catch(() => setProducts([]))
      .finally(() => setLoading(false));
  }, []);

  const selectedProduct = products.find(p => p.product_id === props.product_id);
  const activeVariants = selectedProduct?.variants?.filter(v => v.active) || [];

  const handleProductChange = (productId: string) => {
    const p = products.find(x => x.product_id === productId);
    if (!p) {
      update({ ...props, product_id: '', product_name: '', product_description: '', product_image: '', variant_id: '', variant_label: '', variant_price: 0 });
      return;
    }
    const firstVariant = p.variants?.filter(v => v.active)[0];
    update({
      ...props,
      product_id: p.product_id,
      product_name: p.name,
      product_description: p.short_description || p.description || '',
      product_image: p.cover_image_url || '',
      variant_id: firstVariant?.variant_id || '',
      variant_label: firstVariant?.label || '',
      variant_price: firstVariant?.price || 0,
      currency: firstVariant?.currency || 'USD',
    });
  };

  const handleVariantChange = (variantId: string) => {
    const v = activeVariants.find(x => x.variant_id === variantId);
    if (!v) return;
    update({ ...props, variant_id: v.variant_id, variant_label: v.label, variant_price: v.price, currency: v.currency });
  };

  return (
    <>
      <TextField label="Section title" value={props.title ?? ''} onChange={(v) => update({ ...props, title: v })} />
      <TextField label="Subtitle" value={props.subtitle ?? ''} onChange={(v) => update({ ...props, subtitle: v })} />
      <div style={fieldStyle}>
        <label style={labelStyle}>Product</label>
        {loading ? (
          <p style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>Loading products…</p>
        ) : products.length === 0 ? (
          <p style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', fontStyle: 'italic' }}>
            No products found — create one in the Products module first.
          </p>
        ) : (
          <select value={props.product_id ?? ''} onChange={(e) => handleProductChange(e.target.value)} style={selectStyle}>
            <option value="">— Select a product —</option>
            {products.map((p) => (
              <option key={p.product_id} value={p.product_id}>{p.name}</option>
            ))}
          </select>
        )}
      </div>
      {activeVariants.length > 0 && (
        <div style={fieldStyle}>
          <label style={labelStyle}>Pricing variant</label>
          <select value={props.variant_id ?? ''} onChange={(e) => handleVariantChange(e.target.value)} style={selectStyle}>
            {activeVariants.map((v) => (
              <option key={v.variant_id} value={v.variant_id}>
                {v.label} — {v.currency} {Number(v.price).toFixed(2)}
                {v.billing_type !== 'one_time' ? ` / ${v.billing_type}` : ''}
                {!v.gateway_price_id ? ' ⚠ no Stripe price' : ''}
              </option>
            ))}
          </select>
        </div>
      )}
      <TextField label="Button text" value={props.button_text ?? 'Buy Now'} onChange={(v) => update({ ...props, button_text: v })} />
      <div style={fieldStyle}>
        <label style={labelStyle}>Background color</label>
        <input type="color" value={props.background_color || '#ffffff'} onChange={(e) => update({ ...props, background_color: e.target.value })}
          style={{ width: '48px', height: '32px', border: '1px solid var(--stroke)', borderRadius: '4px', cursor: 'pointer', padding: '2px' }} />
      </div>
      <div style={{ marginBottom: '16px', paddingBottom: '16px', borderBottom: '1px solid var(--stroke)' }}>
        <label style={{ ...labelStyle, marginBottom: '8px' }}>Colours</label>
        <ColorPickerField label="Heading" value={props.title_color || '#0f172a'} onChange={(v) => update({ ...props, title_color: v })} />
        <ColorPickerField label="Subheading" value={props.subtitle_color || '#64748b'} onChange={(v) => update({ ...props, subtitle_color: v })} />
        <ColorPickerField label="Product name" value={props.name_color || '#0f172a'} onChange={(v) => update({ ...props, name_color: v })} />
        <ColorPickerField label="Description" value={props.description_color || '#475569'} onChange={(v) => update({ ...props, description_color: v })} />
        <ColorPickerField label="Price" value={props.price_color || primaryColor} onChange={(v) => update({ ...props, price_color: v })} />
        <ColorPickerField label="Button" value={props.button_color || primaryColor} onChange={(v) => update({ ...props, button_color: v })} />
        <ColorPickerField label="Button text" value={props.button_text_color || '#ffffff'} onChange={(v) => update({ ...props, button_text_color: v })} />
        <ColorPickerField label="Guarantee text" value={props.guarantee_color || '#94a3b8'} onChange={(v) => update({ ...props, guarantee_color: v })} />
        {PRODUCT_COLOR_KEYS.some((k) => !!props[k]) && (
          <button
            type="button"
            style={{ ...smallBtnStyle, marginTop: '6px', fontSize: '0.7rem' }}
            onClick={() => {
              const rest = { ...props };
              PRODUCT_COLOR_KEYS.forEach((k) => delete rest[k]);
              update(rest);
            }}
          >
            Reset to default colours
          </button>
        )}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
        <input type="checkbox" id="show_guarantee" checked={!!props.show_guarantee} onChange={(e) => update({ ...props, show_guarantee: e.target.checked })} />
        <label htmlFor="show_guarantee" style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>Show money-back guarantee</label>
      </div>
      {props.show_guarantee && (
        <TextField label="Guarantee text" value={props.guarantee_text ?? '30-day money-back guarantee'} onChange={(v) => update({ ...props, guarantee_text: v })} />
      )}
    </>
  );
}

// ── Whitepaper picker ───────────────────────────────────────────────────────
// Product-backed like ProductPicker, but filtered to lead-magnet-eligible
// products. Includes both AI-generated whitepapers (source_task_id set by
// whitepaper_service.py) AND manually-uploaded products flagged as lead
// magnets (is_lead_magnet=True) via the Products Store — the download route
// (product_routes.download_file) already prefers a whitepaper-tagged file
// when present and falls back to the first uploaded file otherwise, and
// lead_magnet_delivery.deliver_lead_magnet() only checks is_lead_magnet +
// status='active', so both product shapes are already fully deliverable.
// See specs/completed/whitepaper-module.md Phase 4.

function WhitepaperPicker({ props, update }: {
  props: Record<string, any>;
  update: (p: Record<string, any>) => void;
}) {
  const [products, setProducts] = useState<Array<{
    product_id: string; name: string; cover_image_url?: string;
    source_task_id?: string | null; whitepaper_gated?: boolean;
    generation_status?: string | null; is_lead_magnet?: boolean;
    status?: string;
  }>>([]);
  const [loading, setLoading] = useState(false);
  const selectStyle: React.CSSProperties = {
    width: '100%', padding: '8px 10px',
    border: '1px solid var(--stroke)', borderRadius: '6px',
    fontSize: '0.875rem', background: 'var(--panel)',
    color: 'var(--text)', outline: 'none',
  };

  useEffect(() => {
    setLoading(true);
    apiClient.get('/products/')
      // Eligible = flagged as a lead magnet AND active. A new/regenerating AI
      // whitepaper is created with status='draft' (product_routes.py's
      // generate-whitepaper handler) and only flips to 'active' on explicit
      // publish, so status==='active' alone already excludes not-yet-ready
      // whitepapers without needing to special-case generation_status here.
      .then((res) => setProducts((res.data || []).filter((p: any) => p.is_lead_magnet === true && p.status === 'active')))
      .catch(() => setProducts([]))
      .finally(() => setLoading(false));
  }, []);

  return (
    <>
      <div style={fieldStyle}>
        <label style={labelStyle}>Whitepaper</label>
        {loading ? (
          <p style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>Loading whitepapers…</p>
        ) : products.length === 0 ? (
          <p style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', fontStyle: 'italic' }}>
            No lead magnets found — generate a whitepaper from an article in the Products module,
            or mark an uploaded product as a lead magnet and publish it, then pick it here.
          </p>
        ) : (
          <select
            value={props.product_id ?? ''}
            onChange={(e) => update({ ...props, product_id: e.target.value })}
            style={selectStyle}
          >
            <option value="">— Select a whitepaper —</option>
            {products.map((p) => (
              <option key={p.product_id} value={p.product_id}>{p.name}</option>
            ))}
          </select>
        )}
        <p style={helpTextStyle}>
          Generate a whitepaper from an article in the Products module, or upload any product
          file and flag it as a lead magnet there — either type can be picked here once published.
        </p>
      </div>
      <TextField label="Heading" value={props.heading ?? ''} onChange={(v) => update({ ...props, heading: v })} />
      <TextField label="Subheading" value={props.subheading ?? ''} multiline onChange={(v) => update({ ...props, subheading: v })} />
      <TextField label="Button label" value={props.button_label ?? 'Download Now'} onChange={(v) => update({ ...props, button_label: v })} />
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
        <input
          type="checkbox"
          id="whitepaper_gated"
          checked={props.gated !== false}
          onChange={(e) => update({ ...props, gated: e.target.checked })}
        />
        <label htmlFor="whitepaper_gated" style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
          Gate behind a lead-capture form
        </label>
      </div>
      <p style={helpTextStyle}>
        {props.gated !== false
          ? 'Visitors must submit name + email before the download link is revealed.'
          : 'The download link is public — anyone with the page URL can download it directly.'}
      </p>
      <ImageUploadField
        label="Cover image override (optional)"
        value={props.cover_image_override ?? ''}
        onChange={(v) => update({ ...props, cover_image_override: v })}
      />
    </>
  );
}

// ── Stock variant picker ──────────────────────────────────────────────────────

function StockPicker({ props, update }: {
  props: Record<string, any>;
  update: (p: Record<string, any>) => void;
}) {
  const [products, setProducts] = useState<Array<{
    product_id: string; name: string;
    variants: Array<{ variant_id: string; label: string; price: number; currency: string; active: boolean; gateway_price_id?: string }>;
  }>>([]);
  const [loadingP, setLoadingP] = useState(false);
  const selectStyle: React.CSSProperties = {
    width: '100%', padding: '8px 10px',
    border: '1px solid var(--stroke)', borderRadius: '6px',
    fontSize: '0.875rem', background: 'var(--panel)',
    color: 'var(--text)', outline: 'none',
  };

  useEffect(() => {
    setLoadingP(true);
    apiClient.get('/products/')
      .then((res) => setProducts(res.data || []))
      .catch(() => setProducts([]))
      .finally(() => setLoadingP(false));
  }, []);

  const selectedProduct = products.find(p => p.product_id === props.product_id);
  const activeVariants = selectedProduct?.variants?.filter(v => v.active) || [];

  const handleProductChange = (productId: string) => {
    const p = products.find(x => x.product_id === productId);
    if (!p) { update({ ...props, product_id: '', variant_id: '', stripe_price_id: '' }); return; }
    const first = p.variants?.filter(v => v.active)[0];
    update({ ...props, product_id: p.product_id, variant_id: first?.variant_id || '', stripe_price_id: first?.gateway_price_id || '' });
  };

  const handleVariantChange = (variantId: string) => {
    const v = activeVariants.find(x => x.variant_id === variantId);
    if (!v) return;
    update({ ...props, variant_id: v.variant_id, stripe_price_id: v.gateway_price_id || '' });
  };

  return (
    <>
      <div style={fieldStyle}>
        <label style={labelStyle}>Product</label>
        {loadingP ? (
          <p style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>Loading products…</p>
        ) : products.length === 0 ? (
          <p style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', fontStyle: 'italic' }}>No products found.</p>
        ) : (
          <select value={props.product_id ?? ''} onChange={(e) => handleProductChange(e.target.value)} style={selectStyle}>
            <option value="">— Select a product —</option>
            {products.map((p) => (
              <option key={p.product_id} value={p.product_id}>{p.name}</option>
            ))}
          </select>
        )}
      </div>
      {activeVariants.length > 0 && (
        <div style={fieldStyle}>
          <label style={labelStyle}>Variant</label>
          <select value={props.variant_id ?? ''} onChange={(e) => handleVariantChange(e.target.value)} style={selectStyle}>
            {activeVariants.map((v) => (
              <option key={v.variant_id} value={v.variant_id}>
                {v.label} — {v.currency} {Number(v.price).toFixed(2)}
                {!v.gateway_price_id ? ' ⚠ no Stripe price' : ''}
              </option>
            ))}
          </select>
        </div>
      )}
      {props.stripe_price_id && (
        <p style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', margin: '0 0 8px' }}>
          Price ID: <code style={{ fontSize: '0.7rem' }}>{props.stripe_price_id}</code>
        </p>
      )}
      {!props.stripe_price_id && props.product_id && (
        <p style={{ fontSize: '0.75rem', color: 'var(--warning, #f59e0b)', margin: '0 0 8px' }}>
          Selected variant has no Stripe price — live stock unavailable.
        </p>
      )}
    </>
  );
}

// ── Share section panel ───────────────────────────────────────────────────────

function SharePanel({ props, update }: {
  props: Record<string, any>;
  update: (p: Record<string, any>) => void;
}) {
  const allPlatforms = ['facebook', 'twitter', 'linkedin', 'whatsapp', 'email'];
  const selectedPlatforms: string[] = props.platforms || ['facebook', 'twitter', 'linkedin', 'whatsapp'];

  const togglePlatform = (p: string) => {
    if (selectedPlatforms.includes(p)) {
      update({ ...props, platforms: selectedPlatforms.filter(x => x !== p) });
    } else {
      update({ ...props, platforms: [...selectedPlatforms, p] });
    }
  };

  return (
    <>
      <TextField label="Section title" value={props.title ?? ''} onChange={(v) => update({ ...props, title: v })} />
      <TextField label="Subtitle" value={props.subtitle ?? ''} onChange={(v) => update({ ...props, subtitle: v })} />
      <TextField label="Share message text" value={props.share_text ?? ''} onChange={(v) => update({ ...props, share_text: v })} />
      <div style={fieldStyle}>
        <label style={labelStyle}>Platforms</label>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
          {allPlatforms.map((p) => (
            <label key={p} style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.85rem', color: 'var(--text)', cursor: 'pointer' }}>
              <input type="checkbox" checked={selectedPlatforms.includes(p)} onChange={() => togglePlatform(p)} />
              {p.charAt(0).toUpperCase() + p.slice(1)}
            </label>
          ))}
        </div>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
        <input type="checkbox" id="show_copy_link" checked={!!props.show_copy_link} onChange={(e) => update({ ...props, show_copy_link: e.target.checked })} />
        <label htmlFor="show_copy_link" style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>Show "Copy Link" button</label>
      </div>
      <div style={fieldStyle}>
        <label style={labelStyle}>Button style</label>
        <select
          value={props.button_style || 'filled'}
          onChange={(e) => update({ ...props, button_style: e.target.value })}
          style={{ width: '100%', padding: '8px 10px', border: '1px solid var(--stroke)', borderRadius: '6px', fontSize: '0.875rem', background: 'var(--panel)', color: 'var(--text)', outline: 'none' }}
        >
          <option value="filled">Filled</option>
          <option value="outline">Outline</option>
          <option value="icon_only">Icons only</option>
        </select>
      </div>
    </>
  );
}

// ── Render per-type properties ──────────────────────────────────────────────

// Section-level icon + text colour for card-grid sections (Features / Services).
// Writes the same _icon_color / _text_color props the renderer reads; both are
// optional and fall back to the brand primary / default text when unset.
function CardColorControls({
  props,
  update,
  primaryColor,
}: {
  props: Record<string, any>;
  update: (p: Record<string, any>) => void;
  primaryColor: string;
}) {
  const hasOverride = !!props._icon_color || !!props._text_color;
  return (
    <div style={{ marginBottom: '16px', paddingBottom: '16px', borderBottom: '1px solid var(--stroke)' }}>
      <label style={{ ...labelStyle, marginBottom: '8px' }}>Card colours</label>
      <ColorPickerField
        label="Icon colour"
        value={props._icon_color || primaryColor}
        onChange={(v) => update({ ...props, _icon_color: v })}
      />
      <ColorPickerField
        label="Text colour"
        value={props._text_color || '#0f172a'}
        onChange={(v) => update({ ...props, _text_color: v })}
      />
      {hasOverride && (
        <button
          type="button"
          style={{ ...smallBtnStyle, marginTop: '6px', fontSize: '0.7rem' }}
          onClick={() => {
            const { _icon_color, _text_color, ...rest } = props;
            update(rest);
          }}
        >
          Reset to brand colours
        </button>
      )}
    </div>
  );
}

// ── RevFX visual-effect editor ──────────────────────────────────────────────

/** Parse a manifest prop `type` string of the form "'a' | 'b' | 'c'" into options. */
function parseUnionOptions(typeStr: string): string[] | null {
  if (!typeStr || !typeStr.includes('|')) return null;
  const parts = typeStr.split('|').map((s) => s.trim());
  const opts: string[] = [];
  for (const p of parts) {
    const m = p.match(/^'([^']*)'$/) || p.match(/^"([^"]*)"$/);
    if (!m) return null; // not a pure string-literal union — skip
    opts.push(m[1]);
  }
  return opts.length ? opts : null;
}

/** Render an auto-generated control for one effect prop, driven by manifest metadata. */
function EffectPropControl({
  propKey, meta, value, onChange,
}: {
  propKey: string;
  meta: RevfxPropMeta;
  value: any;
  onChange: (v: any) => void;
}) {
  const label = propKey.replace(/([A-Z])/g, ' $1').replace(/^./, (c) => c.toUpperCase());
  const help = meta.doc;
  const typeStr = (meta.type || '').trim();
  const union = parseUnionOptions(typeStr);

  if (typeStr === 'boolean') {
    return <BooleanField label={label} value={!!value} onChange={onChange} />;
  }
  if (typeStr === 'number') {
    return (
      <>
        <NumberField label={label} value={typeof value === 'number' ? value : Number(meta.default ?? 0)} onChange={onChange} />
        {help && <p style={{ ...helpTextStyle, marginTop: '-12px', marginBottom: '12px' }}>{help}</p>}
      </>
    );
  }
  if (union) {
    return (
      <div style={fieldStyle}>
        <label style={labelStyle}>{label}</label>
        <select style={inputStyle} value={value ?? (meta.default as string) ?? union[0]} onChange={(e) => onChange(e.target.value)}>
          {union.map((o) => <option key={o} value={o}>{o}</option>)}
        </select>
        {help && <p style={helpTextStyle}>{help}</p>}
      </div>
    );
  }
  if (typeStr === 'string') {
    if (/colou?r/i.test(propKey)) {
      return <ColorPickerField label={label} value={(value as string) || (meta.default as string) || '#ffffff'} onChange={onChange} />;
    }
    // Image/asset-typed props get the brand image picker rather than a raw URL field.
    if (/(^|[^a-z])(src|image|img|url|photo|background)([^a-z]|$)/i.test(propKey)) {
      return (
        <>
          <ImageUploadField label={label} value={(value as string) ?? (meta.default as string) ?? ''} onChange={onChange} />
          {help && <p style={{ ...helpTextStyle, marginTop: '-12px', marginBottom: '12px' }}>{help}</p>}
        </>
      );
    }
    return <TextField label={label} value={(value as string) ?? (meta.default as string) ?? ''} onChange={onChange} helpText={help} />;
  }
  // Unsupported/complex types (arrays, objects) are not editable here — skip.
  return null;
}

function EffectEditor({
  props, update, primaryColor, allComponents,
}: {
  props: Record<string, any>;
  update: (p: Record<string, any>) => void;
  primaryColor: string;
  allComponents?: LPComponent[];
}) {
  const effectId = props.effect_id || 'snow';
  const meta = REVFX_BY_ID[effectId];
  const integ = effectIntegration(effectId);
  const effectProps: Record<string, any> = (props.effect_props && typeof props.effect_props === 'object') ? props.effect_props : {};

  const setEffectId = (id: string) => update({ ...props, effect_id: id, effect_props: defaultEffectProps(id) });
  const setEffectProp = (k: string, v: any) => update({ ...props, effect_props: { ...effectProps, [k]: v } });

  // Group effects by category for the picker.
  const groups: Record<string, typeof REVFX_PALETTE> = {};
  for (const e of REVFX_PALETTE) (groups[e.category] ||= []).push(e);

  // Manifest tunables shown in "Effect settings", minus props handled by the
  // kind-specific inputs above (text effect's phrases/text; image-array/items props
  // are rendered inline below).
  const handledKeys = new Set<string>();
  if (integ.kind === 'text') { handledKeys.add('phrases'); handledKeys.add('text'); }
  if (effectId === 'polyfold') handledKeys.add('edge'); // custom select adds a "both" option
  for (const k of integ.imageItemsProps || []) handledKeys.add(k); // rendered as ImageArrayField (__items)

  return (
    <>
      <div style={fieldStyle}>
        <label style={labelStyle}>Choose effect</label>
        <select style={inputStyle} value={effectId} onChange={(e) => setEffectId(e.target.value)}>
          {Object.entries(groups).map(([cat, list]) => (
            <optgroup key={cat} label={cat}>
              {list.map((e) => (
                <option key={e.id} value={e.id}>
                  {e.component}{e.needsThree ? ' (WebGL — heavier)' : e.needsLottie ? ' (Lottie)' : ''}
                </option>
              ))}
            </optgroup>
          ))}
        </select>
        {meta?.summary && <p style={helpTextStyle}>{meta.summary}</p>}
      </div>

      {/* Section background — defaults to dark so light effects + white text show. */}
      <BackgroundEditor props={props} update={update} />

      {/* Kind-specific inputs: the content/image each effect actually needs. */}
      {integ.kind === 'childImage' && (
        <>
          <ImageUploadField label="Image" value={effectProps.__img ?? ''} onChange={(v) => setEffectProp('__img', v)} />
          <div style={fieldStyle}>
            <label style={labelStyle}>Image sizing</label>
            <select style={inputStyle} value={(effectProps.__imgFit ?? 'cover') as string} onChange={(e) => setEffectProp('__imgFit', e.target.value)}>
              <option value="cover">Cover (fill, crop to fit)</option>
              <option value="contain">Contain (show whole image)</option>
            </select>
          </div>
          <div style={fieldStyle}>
            <label style={labelStyle}>Image placement</label>
            <select style={inputStyle} value={(effectProps.__imgAlign ?? 'center') as string} onChange={(e) => setEffectProp('__imgAlign', e.target.value)}>
              <option value="center">Center</option>
              <option value="left">Left</option>
              <option value="right">Right</option>
              <option value="top">Top</option>
              <option value="bottom">Bottom</option>
            </select>
          </div>
        </>
      )}
      {integ.kind === 'beforeAfter' && (
        <>
          <ImageUploadField label="Before image" value={effectProps.__before ?? ''} onChange={(v) => setEffectProp('__before', v)} />
          <ImageUploadField label="After image" value={effectProps.__after ?? ''} onChange={(v) => setEffectProp('__after', v)} />
        </>
      )}
      {(integ.imageItemsProps || []).length > 0 && (
        <ImageArrayField label="Images" values={Array.isArray(effectProps.__items) ? effectProps.__items : []} onChange={(urls) => setEffectProp('__items', urls)} helpText="Media shown in the strip." />
      )}
      {effectId === 'polyfold' && (
        <div style={fieldStyle}>
          <label style={labelStyle}>Decorate edge</label>
          <select style={inputStyle} value={(effectProps.edge ?? 'bottom') as string} onChange={(e) => setEffectProp('edge', e.target.value)}>
            <option value="top">Top</option>
            <option value="bottom">Bottom</option>
            <option value="both">Both</option>
          </select>
        </div>
      )}
      {effectId === 'duotone' && (
        <div style={fieldStyle}>
          <label style={labelStyle}>Image alignment</label>
          <select style={inputStyle} value={(effectProps.__imgAlign ?? 'center') as string} onChange={(e) => setEffectProp('__imgAlign', e.target.value)}>
            <option value="center">Centered</option>
            <option value="left">Left</option>
            <option value="right">Right</option>
          </select>
        </div>
      )}
      {integ.kind === 'text' && (
        <>
          <TextField
            label="Lines (one per line — each replaces the previous on screen)"
            value={(Array.isArray(effectProps.phrases) ? effectProps.phrases : []).join('\n')}
            multiline
            onChange={(v) => setEffectProp('phrases', v.split('\n'))}
          />
          <div style={fieldStyle}>
            <label style={labelStyle}>Text alignment</label>
            <select style={inputStyle} value={(effectProps.__align ?? 'center') as string} onChange={(e) => setEffectProp('__align', e.target.value)}>
              <option value="center">Centered</option>
              <option value="left">Left</option>
              <option value="right">Right</option>
            </select>
          </div>
          <NumberField label="Font size (px)" value={typeof effectProps.__fontSize === 'number' ? effectProps.__fontSize : 48} min={8} onChange={(v) => setEffectProp('__fontSize', v)} />
          <ColorPickerField label="Text colour" value={(effectProps.__color as string) || props.overlay_color || '#ffffff'} onChange={(v) => setEffectProp('__color', v)} />
        </>
      )}

      {effectId === 'whiteboard' && (
        <>
          <TextField label="Text to draw" value={effectProps.__text ?? ''} multiline onChange={(v) => setEffectProp('__text', v)} />
          <div style={fieldStyle}>
            <label style={labelStyle}>Text alignment</label>
            <select style={inputStyle} value={(effectProps.__align ?? 'center') as string} onChange={(e) => setEffectProp('__align', e.target.value)}>
              <option value="center">Centered</option>
              <option value="left">Left</option>
              <option value="right">Right</option>
            </select>
          </div>
          <NumberField label="Font size (px)" value={typeof effectProps.__fontSize === 'number' ? effectProps.__fontSize : 48} min={8} onChange={(v) => setEffectProp('__fontSize', v)} />
          <ColorPickerField label="Text colour" value={(effectProps.__color as string) || props.overlay_color || '#ffffff'} onChange={(v) => setEffectProp('__color', v)} />
        </>
      )}

      {meta && Object.keys(meta.props).length > 0 && (
        <div style={{ ...fieldStyle, borderTop: '1px solid var(--stroke)', paddingTop: '12px' }}>
          <label style={{ ...labelStyle, marginBottom: '8px' }}>Effect settings</label>
          {Object.entries(meta.props).filter(([key]) => !handledKeys.has(key)).map(([key, pm]) => {
            if (isEffectImageArrayProp(effectId, key)) {
              return <ImageArrayField key={key} label={key === 'images' ? 'Images' : key === 'frames' ? 'Frames (image sequence)' : key}
                values={Array.isArray(effectProps[key]) ? effectProps[key] : []} onChange={(urls) => setEffectProp(key, urls)} helpText={pm.doc} />;
            }
            if (isEffectImageProp(effectId, key)) {
              return (
                <React.Fragment key={key}>
                  <ImageUploadField label={key === 'source' ? 'Source image' : key === 'displacementMap' ? 'Displacement map' : key === 'src' ? 'Image' : key}
                    value={(effectProps[key] as string) ?? ''} onChange={(v) => setEffectProp(key, v)} />
                  {pm.doc && <p style={{ ...helpTextStyle, marginTop: '-12px', marginBottom: '12px' }}>{pm.doc}</p>}
                </React.Fragment>
              );
            }
            return <EffectPropControl key={key} propKey={key} meta={pm} value={effectProps[key]} onChange={(v) => setEffectProp(key, v)} />;
          })}
        </div>
      )}

      <div style={{ ...fieldStyle, borderTop: '1px solid var(--stroke)', paddingTop: '12px' }}>
        <label style={{ ...labelStyle, marginBottom: '8px' }}>Text overlay</label>
        <TextField label="Heading" value={props.overlay_heading ?? ''} onChange={(v) => update({ ...props, overlay_heading: v })} />
        <TextField label="Subheading" value={props.overlay_subheading ?? ''} multiline onChange={(v) => update({ ...props, overlay_subheading: v })} />
        <BooleanField label="Show CTA button" value={props.overlay_show_cta ?? !!props.overlay_cta_text} onChange={(v) => update({ ...props, overlay_show_cta: v })} />
        {(props.overlay_show_cta ?? !!props.overlay_cta_text) && (
          <>
            <TextField label="CTA button text" value={props.overlay_cta_text ?? ''} onChange={(v) => update({ ...props, overlay_cta_text: v })} />
            <LinkPicker label="CTA link" value={props.overlay_cta_url ?? '#lead-form'} onChange={(v) => update({ ...props, overlay_cta_url: v })} allComponents={allComponents} />
            <ColorPickerField label="CTA button colour" value={props.overlay_cta_color || primaryColor} onChange={(v) => update({ ...props, overlay_cta_color: v })} />
          </>
        )}
        <div style={fieldStyle}>
          <label style={labelStyle}>Text alignment (horizontal)</label>
          <select style={inputStyle} value={(props.overlay_align ?? 'center') as string} onChange={(e) => update({ ...props, overlay_align: e.target.value })}>
            <option value="left">Left</option>
            <option value="center">Center</option>
            <option value="right">Right</option>
          </select>
        </div>
        <div style={fieldStyle}>
          <label style={labelStyle}>Vertical position</label>
          <select style={inputStyle} value={(props.overlay_valign ?? 'center') as string} onChange={(e) => update({ ...props, overlay_valign: e.target.value })}>
            <option value="top">Top</option>
            <option value="center">Center</option>
            <option value="bottom">Bottom</option>
          </select>
        </div>
        {(props.overlay_show_cta ?? !!props.overlay_cta_text) && (
          <div style={fieldStyle}>
            <label style={labelStyle}>CTA button alignment</label>
            <select style={inputStyle} value={(props.overlay_cta_align ?? 'center') as string} onChange={(e) => update({ ...props, overlay_cta_align: e.target.value })}>
              <option value="left">Left</option>
              <option value="center">Center</option>
              <option value="right">Right</option>
            </select>
          </div>
        )}
        <ColorPickerField label="Overlay text colour" value={props.overlay_color || '#ffffff'} onChange={(v) => update({ ...props, overlay_color: v })} />
        <NumberField label="Min height (px)" value={typeof props.min_height === 'number' ? props.min_height : 480} min={120} onChange={(v) => update({ ...props, min_height: v })} />
      </div>
    </>
  );
}

function renderFields(
  component: LPComponent,
  onChange: (id: string, newProps: Record<string, any>) => void,
  projectId?: string | null,
  secondaryColor: string = '#2177FF',
  allComponents?: LPComponent[],
  primaryColor: string = '#2177FF'
) {
  const { id, type, props } = component;
  const update = (newProps: Record<string, any>) => onChange(id, newProps);

  switch (type) {
    case 'lp_hero':
      return <HeroEditor props={props} update={update} secondaryColor={secondaryColor} allComponents={allComponents} projectId={projectId} />;

    case 'lp_features':
      return (
        <>
          <TextField
            label="Section title"
            value={props.title ?? ''}
            onChange={(v) => update({ ...props, title: v })}
          />
          <CardColorControls props={props} update={update} primaryColor={primaryColor} />
          <ItemsEditor
            label="Features"
            items={props.items ?? []}
            fields={[
              { key: 'icon', label: 'Icon name' },
              { key: 'title', label: 'Title' },
              { key: 'description', label: 'Description', multiline: true },
            ]}
            onUpdate={(items) => update({ ...props, items })}
          />
        </>
      );

    case 'lp_services':
      return (
        <>
          <TextField
            label="Section title"
            value={props.title ?? ''}
            onChange={(v) => update({ ...props, title: v })}
          />
          <CardColorControls props={props} update={update} primaryColor={primaryColor} />
          <ItemsEditor
            label="Services"
            items={props.items ?? []}
            fields={[
              { key: 'icon', label: 'Icon name' },
              { key: 'title', label: 'Title' },
              { key: 'description', label: 'Description', multiline: true },
            ]}
            onUpdate={(items) => update({ ...props, items })}
          />
        </>
      );

    case 'lp_stats':
      return (
        <>
          <ColorPickerField
            label="Value colour"
            value={props.value_color || '#ffffff'}
            onChange={(v) => update({ ...props, value_color: v })}
          />
          <ItemsEditor
            label="Stats"
            items={props.items ?? []}
            fields={[
              { key: 'value', label: 'Value (e.g. 500+)' },
              { key: 'label', label: 'Label' },
            ]}
            onUpdate={(items) => update({ ...props, items })}
          />
        </>
      );

    case 'lp_process': {
      // Normalise old string[] format to {title, description}[]
      const processSteps = (props.steps || []).map((s: any) =>
        typeof s === 'string' ? { title: s, description: '' } : s
      );
      return (
        <>
          <TextField
            label="Section title"
            value={props.title ?? ''}
            onChange={(v) => update({ ...props, title: v })}
          />
          <ItemsEditor
            label="Steps"
            items={processSteps}
            fields={[
              { key: 'title', label: 'Step title' },
              { key: 'description', label: 'Description', multiline: true },
            ]}
            onUpdate={(steps) => update({ ...props, steps })}
          />
        </>
      );
    }

    case 'lp_testimonials':
      return (
        <>
          <TextField
            label="Section title"
            value={props.title ?? ''}
            onChange={(v) => update({ ...props, title: v })}
          />
          <TestimonialsItemsEditor
            items={props.items ?? []}
            onUpdate={(items) => update({ ...props, items })}
          />
        </>
      );

    case 'lp_faq':
      return (
        <>
          <TextField
            label="Section title"
            value={props.title ?? ''}
            onChange={(v) => update({ ...props, title: v })}
          />
          <ItemsEditor
            label="FAQs"
            items={props.items ?? []}
            fields={[
              { key: 'question', label: 'Question' },
              { key: 'answer', label: 'Answer', multiline: true },
            ]}
            onUpdate={(items) => update({ ...props, items })}
          />
        </>
      );

    case 'lp_quiz':
      return <QuizPicker props={props} update={update} projectId={projectId} />;

    case 'lp_lead_form':
      return (
        <>
          <TextField
            label="Form title"
            value={props.title ?? ''}
            onChange={(v) => update({ ...props, title: v })}
          />
          <TextField
            label="Subtitle"
            value={props.subtitle ?? ''}
            multiline
            onChange={(v) => update({ ...props, subtitle: v })}
          />
          <TextField
            label="Submit button label"
            value={props.submit_label ?? ''}
            onChange={(v) => update({ ...props, submit_label: v })}
          />
          <ColorPickerField
            label="Button colour"
            value={props.button_color || secondaryColor}
            onChange={(v) => update({ ...props, button_color: v })}
          />
          <LeadFormPicker label="Lead capture form" propKey="form_id" props={props} update={update} projectId={projectId} mode="id" />
          <TextField
            label="Landing Page ID (set by system)"
            value={props.landing_page_id ?? ''}
            readonly
          />
        </>
      );

    case 'lp_cta': {
      const mode = (props.display_mode ?? 'button') as 'button' | 'inline_form';
      return (
        <>
          <div style={fieldStyle}>
            <label style={labelStyle}>Display mode</label>
            <select
              style={inputStyle}
              value={mode}
              onChange={(e) => update({ ...props, display_mode: e.target.value })}
            >
              <option value="button">Button (scroll to anchor)</option>
              <option value="inline_form">Inline form (collect leads here)</option>
            </select>
          </div>

          <TextField
            label="Headline"
            value={props.title ?? ''}
            onChange={(v) => update({ ...props, title: v })}
          />
          <TextField
            label="Subtitle"
            value={props.subtitle ?? ''}
            multiline
            onChange={(v) => update({ ...props, subtitle: v })}
          />

          {mode === 'button' && (
            <>
              <TextField
                label="Button text"
                value={props.button_text ?? ''}
                onChange={(v) => update({ ...props, button_text: v })}
              />
              <LinkPicker label="Button link" value={props.button_url ?? '#lead-form'} onChange={(v) => update({ ...props, button_url: v })} allComponents={allComponents} />
              <ColorPickerField
                label="Button colour"
                value={props.button_color || secondaryColor}
                onChange={(v) => update({ ...props, button_color: v })}
              />
              <ColorPickerField
                label="Button shadow colour"
                value={props.button_shadow_color || secondaryColor}
                onChange={(v) => update({ ...props, button_shadow_color: v })}
              />
            </>
          )}

          {mode === 'inline_form' && (
            <>
              <TextField
                label="Form title (optional — overrides headline inside the form)"
                value={props.form_title ?? ''}
                onChange={(v) => update({ ...props, form_title: v })}
              />
              <LeadFormPicker
                label="Lead capture form"
                propKey="form_id"
                props={props}
                update={update}
                projectId={projectId}
                mode="id"
              />
              <TextField
                label="Submit button text (optional — overrides form default)"
                value={props.submit_button_text ?? ''}
                onChange={(v) => update({ ...props, submit_button_text: v })}
              />
              <ColorPickerField
                label="Submit button colour"
                value={props.button_color || secondaryColor}
                onChange={(v) => update({ ...props, button_color: v })}
              />
            </>
          )}
        </>
      );
    }

    case 'lp_pricing': {
      const tiers = props.items || [];
      const updateTier = (idx: number, field: string, val: any) => {
        const newTiers = tiers.map((t: any, i: number) => i === idx ? { ...t, [field]: val } : t);
        update({ ...props, items: newTiers });
      };
      const addTier = () => {
        update({ ...props, items: [...tiers, { name: `Tier ${tiers.length + 1}`, price: '$99', period: 'mo', features: ['Feature 1'], cta_text: 'Get Started', cta_url: '#lead-form', highlighted: false }] });
      };
      const removeTier = (idx: number) => {
        update({ ...props, items: tiers.filter((_: any, i: number) => i !== idx) });
      };
      return (
        <>
          <TextField label="Section title" value={props.title ?? ''} onChange={(v) => update({ ...props, title: v })} />
          <TextField label="Subtitle" value={props.subtitle ?? ''} onChange={(v) => update({ ...props, subtitle: v })} />
          <BooleanField label="Pill-shaped buttons" value={props.pill_buttons !== false} onChange={(v) => update({ ...props, pill_buttons: v })} />

          <div style={{ marginTop: '8px' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
              <label style={{ fontSize: '0.8rem', fontWeight: 600, color: 'var(--text)' }}>Pricing Tiers ({tiers.length})</label>
              <button onClick={addTier} style={{ background: 'none', border: 'none', color: 'var(--primary-500, #2177FF)', cursor: 'pointer', fontSize: '0.8rem', fontWeight: 600 }}>+ Add Tier</button>
            </div>
            {tiers.map((tier: any, idx: number) => (
              <div key={idx} style={{
                background: 'var(--panel-strong)', border: '1px solid var(--stroke)',
                borderRadius: '8px', padding: '12px', marginBottom: '8px',
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                  <span style={{ fontSize: '0.78rem', fontWeight: 600, color: 'var(--text)' }}>Tier {idx + 1}</span>
                  <button onClick={() => removeTier(idx)} style={{ background: 'none', border: 'none', color: 'var(--danger, #dc2626)', cursor: 'pointer', fontSize: '0.75rem' }}>Remove</button>
                </div>
                <TextField label="Name" value={tier.name ?? ''} onChange={(v) => updateTier(idx, 'name', v)} />
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px' }}>
                  <TextField label="Price" value={tier.price ?? ''} onChange={(v) => updateTier(idx, 'price', v)} />
                  <TextField label="Period" value={tier.period ?? ''} onChange={(v) => updateTier(idx, 'period', v)} placeholder="mo, yr, etc." />
                </div>
                <TextField label="Features (one per line)" value={(tier.features || []).join('\n')} multiline onChange={(v) => updateTier(idx, 'features', v.split('\n').filter((l: string) => l.trim()))} />
                <TextField label="Button text" value={tier.cta_text ?? 'Get Started'} onChange={(v) => updateTier(idx, 'cta_text', v)} />
                <TextField label="Button URL" value={tier.cta_url ?? '#lead-form'} onChange={(v) => updateTier(idx, 'cta_url', v)} />
                <BooleanField label="Highlight (featured)" value={!!tier.highlighted} onChange={(v) => updateTier(idx, 'highlighted', v)} />
              </div>
            ))}
          </div>
        </>
      );
    }

    case 'lp_logo_cloud':
      return (
        <>
          <TextField
            label="Section title"
            value={props.title ?? ''}
            onChange={(v) => update({ ...props, title: v })}
          />
          <LogoCloudEditor
            logos={props.logos ?? []}
            onUpdate={(logos) => update({ ...props, logos })}
          />
        </>
      );

    case 'lp_video':
      return (
        <>
          <TextField
            label="Section title"
            value={props.title ?? ''}
            onChange={(v) => update({ ...props, title: v })}
          />
          <TextField
            label="Video URL"
            value={props.video_url ?? ''}
            type="url"
            onChange={(v) => update({ ...props, video_url: v })}
          />
          <TextField
            label="Thumbnail URL (optional)"
            value={props.thumbnail_url ?? ''}
            type="url"
            onChange={(v) => update({ ...props, thumbnail_url: v })}
          />
        </>
      );

    case 'lp_scarcity_timer':
      return (
        <>
          <TextField
            label="Title"
            value={props.title ?? ''}
            onChange={(v) => update({ ...props, title: v })}
          />
          <TextField
            label="Subtitle"
            value={props.subtitle ?? ''}
            onChange={(v) => update({ ...props, subtitle: v })}
          />
          <TextField
            label="Deadline (date & time)"
            value={props.end_datetime ?? ''}
            type="datetime-local"
            onChange={(v) => update({ ...props, end_datetime: v })}
          />
          <TextField
            label="Redirect URL on expiry"
            value={props.expired_redirect ?? '/offer-expired'}
            type="url"
            placeholder="/offer-expired"
            onChange={(v) => update({ ...props, expired_redirect: v })}
            helpText="Visitor is redirected here when the timer hits zero. Defaults to the built-in expired page which sends them back automatically."
          />
          <TextField
            label="Expired message (if no redirect)"
            value={props.expired_message ?? ''}
            multiline
            onChange={(v) => update({ ...props, expired_message: v })}
          />
          <TextField
            label="Background colour"
            value={props.background_color ?? '#e53e3e'}
            type="color"
            onChange={(v) => update({ ...props, background_color: v })}
          />
          <BooleanField
            label="Show days"
            value={props.show_days !== false}
            onChange={(v) => update({ ...props, show_days: v })}
          />
        </>
      );

    case 'lp_vsl':
      return (
        <>
          <TextField
            label="Title (optional)"
            value={props.title ?? ''}
            onChange={(v) => update({ ...props, title: v })}
          />
          <TextField
            label="Video URL"
            value={props.video_url ?? ''}
            type="url"
            onChange={(v) => update({ ...props, video_url: v })}
          />
          <TextField
            label="Poster / thumbnail URL"
            value={props.poster_url ?? ''}
            type="url"
            onChange={(v) => update({ ...props, poster_url: v })}
          />
          <TextField
            label="CTA button text"
            value={props.cta_text ?? ''}
            onChange={(v) => update({ ...props, cta_text: v })}
          />
          <LinkPicker label="CTA link" value={props.cta_url ?? ''} onChange={(v) => update({ ...props, cta_url: v })} allComponents={allComponents} />
          <ColorPickerField
            label="Button colour"
            value={props.button_color || secondaryColor}
            onChange={(v) => update({ ...props, button_color: v })}
          />
          <NumberField
            label="Show CTA after N seconds (0 = always)"
            value={props.show_cta_after_seconds ?? 0}
            min={0}
            onChange={(v) => update({ ...props, show_cta_after_seconds: v })}
          />
          <BooleanField
            label="Autoplay"
            value={!!props.autoplay}
            onChange={(v) => update({ ...props, autoplay: v })}
          />
          <BooleanField
            label="Hide controls"
            value={!!props.hide_controls}
            onChange={(v) => update({ ...props, hide_controls: v })}
          />
        </>
      );

    case 'lp_stock':
      return (
        <>
          <TextField
            label="Title"
            value={props.title ?? ''}
            onChange={(v) => update({ ...props, title: v })}
          />
          <StockPicker props={props} update={update} />
          <TextField
            label="Unit label (e.g. spots remaining)"
            value={props.unit_label ?? ''}
            onChange={(v) => update({ ...props, unit_label: v })}
          />
          <NumberField
            label="Current stock (fallback)"
            value={props.current_stock ?? 7}
            min={0}
            onChange={(v) => update({ ...props, current_stock: v })}
          />
          <NumberField
            label="Max stock (fallback)"
            value={props.max_stock ?? 20}
            min={1}
            onChange={(v) => update({ ...props, max_stock: v })}
          />
          <NumberField
            label="Urgency threshold (turns red)"
            value={props.urgency_threshold ?? 5}
            min={0}
            onChange={(v) => update({ ...props, urgency_threshold: v })}
          />
          <BooleanField
            label="Show progress bar"
            value={props.show_progress_bar !== false}
            onChange={(v) => update({ ...props, show_progress_bar: v })}
          />
          <TextField
            label="Sold out label"
            value={props.sold_out_label ?? ''}
            onChange={(v) => update({ ...props, sold_out_label: v })}
            placeholder="Sold Out"
          />
          <TextField
            label="Sold out message"
            value={props.sold_out_message ?? ''}
            onChange={(v) => update({ ...props, sold_out_message: v })}
            placeholder="Join the waitlist to be notified..."
          />
        </>
      );

    case 'lp_exit_popup':
      return (
        <>
          <TextField
            label="Headline"
            value={props.headline ?? ''}
            onChange={(v) => update({ ...props, headline: v })}
          />
          <TextField
            label="Body text"
            value={props.body_text ?? ''}
            multiline
            onChange={(v) => update({ ...props, body_text: v })}
          />
          <TextField
            label="CTA button text"
            value={props.cta_text ?? ''}
            onChange={(v) => update({ ...props, cta_text: v })}
          />
          <LinkPicker label="CTA link" value={props.cta_url ?? ''} onChange={(v) => update({ ...props, cta_url: v })} allComponents={allComponents} />
          <ColorPickerField
            label="Button colour"
            value={props.button_color || secondaryColor}
            onChange={(v) => update({ ...props, button_color: v })}
          />
          <TextField
            label="Dismiss link label"
            value={props.dismiss_label ?? ''}
            onChange={(v) => update({ ...props, dismiss_label: v })}
          />
          <NumberField
            label="Trigger after N seconds (0 = on exit-intent)"
            value={props.trigger_after_seconds ?? 0}
            min={0}
            onChange={(v) => update({ ...props, trigger_after_seconds: v })}
          />
          <BooleanField
            label="Show once per session"
            value={props.show_once_per_session !== false}
            onChange={(v) => update({ ...props, show_once_per_session: v })}
          />
        </>
      );

    case 'lp_social_proof_popup':
      return (
        <>
          <TextField
            label="Title"
            value={props.title ?? ''}
            onChange={(v) => update({ ...props, title: v })}
          />
          <NumberField
            label="Display interval (seconds)"
            value={props.display_interval_seconds ?? 5}
            min={1}
            onChange={(v) => update({ ...props, display_interval_seconds: v })}
          />
          <BooleanField
            label="Show avatar"
            value={props.show_avatar !== false}
            onChange={(v) => update({ ...props, show_avatar: v })}
          />
          <ItemsEditor
            label="Notifications"
            items={props.notifications ?? []}
            fields={[
              { key: 'name', label: 'Name' },
              { key: 'action', label: 'Action (e.g. booked a consultation)' },
              { key: 'location', label: 'Location' },
              { key: 'time_ago', label: 'Time ago (e.g. 2 hours ago)' },
            ]}
            onUpdate={(notifications) => update({ ...props, notifications })}
          />
        </>
      );

    case 'lp_authority':
      return (
        <>
          <TextField
            label="Title"
            value={props.title ?? ''}
            onChange={(v) => update({ ...props, title: v })}
          />
          <TextField
            label="Subtitle (optional)"
            value={props.subtitle ?? ''}
            onChange={(v) => update({ ...props, subtitle: v })}
          />
          <BooleanField
            label="Show logos section"
            value={props.show_logos !== false}
            onChange={(v) => update({ ...props, show_logos: v })}
          />
          <BooleanField
            label="Show credentials section"
            value={props.show_credentials !== false}
            onChange={(v) => update({ ...props, show_credentials: v })}
          />
          <ItemsEditor
            label="Logos"
            items={props.logos ?? []}
            fields={[
              { key: 'name', label: 'Name / alt text' },
              { key: 'logo_url', label: 'Logo URL' },
            ]}
            onUpdate={(logos) => update({ ...props, logos })}
          />
          <ItemsEditor
            label="Credentials"
            items={props.credentials ?? []}
            fields={[
              { key: 'icon', label: 'Icon name (e.g. award, shield, star)' },
              { key: 'label', label: 'Credential label' },
            ]}
            onUpdate={(credentials) => update({ ...props, credentials })}
          />
        </>
      );

    case 'lp_roi_calculator':
      return (
        <>
          <TextField
            label="Title"
            value={props.title ?? ''}
            onChange={(v) => update({ ...props, title: v })}
          />
          <TextField
            label="Subtitle"
            value={props.subtitle ?? ''}
            onChange={(v) => update({ ...props, subtitle: v })}
          />
          <TextField
            label="Formula (use input IDs as variables)"
            value={props.formula ?? ''}
            multiline
            onChange={(v) => update({ ...props, formula: v })}
          />
          <TextField
            label="Output label"
            value={props.output_label ?? ''}
            onChange={(v) => update({ ...props, output_label: v })}
          />
          <TextField
            label="Output prefix (e.g. $)"
            value={props.output_prefix ?? ''}
            onChange={(v) => update({ ...props, output_prefix: v })}
          />
          <TextField
            label="Output suffix (e.g. /yr)"
            value={props.output_suffix ?? ''}
            onChange={(v) => update({ ...props, output_suffix: v })}
          />
          <TextField
            label="CTA button text"
            value={props.cta_text ?? ''}
            onChange={(v) => update({ ...props, cta_text: v })}
          />
          <LinkPicker label="CTA link" value={props.cta_url ?? ''} onChange={(v) => update({ ...props, cta_url: v })} allComponents={allComponents} />
          <ColorPickerField
            label="Result box background"
            value={props.result_bg_color || secondaryColor}
            onChange={(v) => update({ ...props, result_bg_color: v })}
          />
          <ColorPickerField
            label="Result text colour"
            value={props.result_text_color || '#ffffff'}
            onChange={(v) => update({ ...props, result_text_color: v })}
          />
          <ItemsEditor
            label="Inputs"
            items={props.inputs ?? []}
            fields={[
              { key: 'id', label: 'ID (used in formula, no spaces)' },
              { key: 'label', label: 'Display label' },
              { key: 'type', label: 'Type (slider or number)' },
              { key: 'min', label: 'Min' },
              { key: 'max', label: 'Max' },
              { key: 'default', label: 'Default value' },
              { key: 'prefix', label: 'Prefix (e.g. $)' },
              { key: 'suffix', label: 'Suffix (e.g. /mo)' },
            ]}
            onUpdate={(inputs) => update({ ...props, inputs })}
          />
        </>
      );

    case 'lp_header':
      return (
        <>
          <TextField label="Company name" value={props.company_name ?? ''} onChange={(v) => update({ ...props, company_name: v })} />
          <ImageUploadField label="Logo" value={props.logo_url ?? ''} onChange={(v) => update({ ...props, logo_url: v })} />
          <TextField label="Phone number (optional)" value={props.phone ?? ''} onChange={(v) => update({ ...props, phone: v })} />
          <TextField label="CTA button text" value={props.cta_text ?? ''} onChange={(v) => update({ ...props, cta_text: v })} />
          <LinkPicker label="CTA link" value={props.cta_url ?? ''} onChange={(v) => update({ ...props, cta_url: v })} allComponents={allComponents} />
          <ColorPickerField
            label="Button colour"
            value={props.button_color || secondaryColor}
            onChange={(v) => update({ ...props, button_color: v })}
          />
          <BooleanField label="Sticky header" value={props.sticky !== false} onChange={(v) => update({ ...props, sticky: v })} />
          <ItemsEditor
            label="Nav links"
            items={props.nav_links ?? []}
            fields={[
              { key: 'label', label: 'Link label' },
              { key: 'url', label: 'URL' },
            ]}
            onUpdate={(nav_links) => update({ ...props, nav_links })}
          />
        </>
      );

    case 'lp_footer':
      return (
        <>
          <ColorPickerField
            label="Background colour"
            value={props._bg_color || '#0f172a'}
            onChange={(v) => update({ ...props, _bg_type: 'solid', _bg_color: v })}
          />
          <ImageUploadField label="Logo" value={props.logo_url ?? ''} onChange={(v) => update({ ...props, logo_url: v })} />
          <TextField label="Company name" value={props.company_name ?? ''} onChange={(v) => update({ ...props, company_name: v })} />
          <TextField label="Tagline" value={props.tagline ?? ''} onChange={(v) => update({ ...props, tagline: v })} />
          <TextField label="Phone" value={props.phone ?? ''} onChange={(v) => update({ ...props, phone: v })} />
          <TextField label="Email" value={props.email ?? ''} onChange={(v) => update({ ...props, email: v })} />
          <TextField label="Address" value={props.address ?? ''} multiline onChange={(v) => update({ ...props, address: v })} />
          <TextField label="Opening hours" value={props.opening_hours ?? ''} multiline onChange={(v) => update({ ...props, opening_hours: v })} />
          <TextField label="Copyright text" value={props.copyright ?? ''} onChange={(v) => update({ ...props, copyright: v })} />
          <ItemsEditor
            label="Footer links"
            items={props.links ?? []}
            fields={[
              { key: 'label', label: 'Link label' },
              { key: 'url', label: 'URL' },
            ]}
            onUpdate={(links) => update({ ...props, links })}
          />
        </>
      );

    case 'lp_product':
      return <ProductPicker props={props} update={update} primaryColor={primaryColor} />;

    case 'lp_whitepaper':
      return <WhitepaperPicker props={props} update={update} />;

    case 'lp_share':
      return <SharePanel props={props} update={update} />;

    case 'lp_text':
      return (
        <>
          <BackgroundEditor props={props} update={update} />
          <TextField
            label="Heading"
            value={props.heading ?? ''}
            placeholder="Leave blank to hide the heading"
            onChange={(v) => update({ ...props, heading: v })}
          />
          <TextField
            label="Body text"
            value={props.body ?? ''}
            multiline
            onChange={(v) => update({ ...props, body: v })}
          />
          <div style={fieldStyle}>
            <label style={labelStyle}>Alignment</label>
            <select
              style={inputStyle}
              value={(props.align ?? 'left') as string}
              onChange={(e) => update({ ...props, align: e.target.value })}
            >
              <option value="left">Left</option>
              <option value="center">Centered</option>
            </select>
          </div>
          <div style={fieldStyle}>
            <label style={labelStyle}>Max width</label>
            <select
              style={inputStyle}
              value={(props.max_width ?? 'narrow') as string}
              onChange={(e) => update({ ...props, max_width: e.target.value })}
            >
              <option value="narrow">Narrow (reading column)</option>
              <option value="wide">Wide</option>
            </select>
          </div>
        </>
      );

    case 'lp_effect':
      return <EffectEditor props={props} update={update} primaryColor={primaryColor} allComponents={allComponents} />;

    default:
      return <p style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>No editable properties.</p>;
  }
}

// ── Main component ──────────────────────────────────────────────────────────

const LPPropertiesPanel: React.FC<LPPropertiesPanelProps> = ({
  component,
  onChange,
  projectId,
  brandTheme,
  allComponents,
}) => {
  const secondaryColor = brandTheme?.secondaryColor || '#2177FF';
  const primaryColor = brandTheme?.primaryColor || '#2177FF';
  if (!component) {
    return (
      <div
        style={{
          padding: '24px 16px',
          textAlign: 'center',
          color: 'var(--text-secondary)',
        }}
      >
        <div style={{ fontSize: '2rem', marginBottom: '12px', opacity: 0.4 }}>◧</div>
        <p style={{ margin: 0, fontSize: '0.875rem' }}>
          Select a section to edit its properties
        </p>
      </div>
    );
  }

  const paletteItem = LP_COMPONENT_PALETTE.find((p) => p.type === component.type);
  const update = (newProps: Record<string, any>) => onChange(component.id, newProps);

  return (
    <div style={{ padding: '16px' }}>
      <h4 style={sectionTitleStyle}>{paletteItem?.label ?? component.type}</h4>
      {renderFields(component, onChange, projectId, secondaryColor, allComponents, primaryColor)}
      <BackgroundEditor props={component.props} update={update} />
    </div>
  );
};

export default LPPropertiesPanel;
